import React, { useState, useMemo, useEffect, useContext, useCallback } from 'react';
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  ClockIcon,
  ArrowPathIcon,
  SignalIcon,
} from '@heroicons/react/24/outline';
import { KeycloakContext } from '../App';
import { useFeatures } from '../providers/FeaturesProvider';
import { API_URL } from '../runtimeConfig';
import {
  PageHeader,
  Card,
  CardContent,
  Button,
  Skeleton,
  ErrorAlert,
  EmptyState,
  StatusBadge,
} from '../components/ui';
import { formatTime } from '../lib/format';
import { cn } from '../lib/cn';

interface Monitor {
  id: string | number;
  name: string;
  status: 'up' | 'down' | 'pending' | string;
  heartbeats?: (0 | 1)[];
  uptime_24h?: number;
  response_time_ms?: number;
  last_checked?: string;
}

function overallStatus(monitors: Monitor[]): 'all_up' | 'some_down' | 'pending' {
  if (!monitors || monitors.length === 0) return 'pending';
  if (monitors.every((m) => m.status === 'up')) return 'all_up';
  if (monitors.some((m) => m.status === 'down')) return 'some_down';
  return 'pending';
}

function formatUptime(value?: number | null) {
  if (value === undefined || value === null || value < 0) return '—';
  return value.toFixed(value === 100 ? 0 : 2) + '%';
}

function timeAgo(dateStr?: string) {
  if (!dateStr) return '';
  const diff = Math.round((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return `${Math.floor(diff / 3600)}h ago`;
}

function HeartbeatBar({ heartbeats = [], total = 50 }: { heartbeats?: (0 | 1)[]; total?: number }) {
  const padded: (0 | 1 | null)[] = Array(Math.max(0, total - heartbeats.length))
    .fill(null)
    .concat(heartbeats) as (0 | 1 | null)[];

  return (
    <div className="flex items-end gap-px h-8 overflow-hidden rounded-sm">
      {padded.map((v, i) => {
        const colorClass =
          v === null ? 'bg-muted' : v === 1 ? 'bg-success' : 'bg-destructive';
        return (
          <div
            key={i}
            title={v === null ? 'No data' : v === 1 ? 'Up' : 'Down'}
            className={cn('flex-1 rounded-sm transition-colors', colorClass)}
            style={{ minWidth: 4, height: v === null ? '40%' : '100%' }}
          />
        );
      })}
    </div>
  );
}

function MonitorRow({ monitor, heartbeatPoints }: { monitor: Monitor; heartbeatPoints: number }) {
  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-center justify-between gap-4 mb-3">
          <div className="flex items-center gap-3 min-w-0">
            <StatusBadge status={monitor.status} />
            <span className="text-sm font-medium text-foreground truncate">{monitor.name}</span>
          </div>
          <div className="flex items-center gap-5 shrink-0 text-xs text-muted-foreground">
            {monitor.response_time_ms != null && monitor.response_time_ms > 0 && (
              <span className="hidden sm:inline tabular-nums">{monitor.response_time_ms} ms</span>
            )}
            {monitor.uptime_24h != null && monitor.uptime_24h > 0 && (
              <span className="hidden md:inline font-medium text-foreground tabular-nums">
                {formatUptime(monitor.uptime_24h)}
              </span>
            )}
            {monitor.last_checked && (
              <span className="hidden xl:inline">{timeAgo(monitor.last_checked)}</span>
            )}
          </div>
        </div>
        <HeartbeatBar heartbeats={monitor.heartbeats} total={heartbeatPoints} />
        <div className="flex justify-between mt-1 text-[10px] text-muted-foreground">
          <span>1h ago</span>
          <span>now</span>
        </div>
      </CardContent>
    </Card>
  );
}

const REFRESH_INTERVAL = 60;

export default function Status() {
  const ctx = useContext(KeycloakContext);
  const keycloak = ctx?.keycloak;

  const [allMonitors, setAllMonitors] = useState<Monitor[]>([]);
  const { features } = useFeatures();
  const monitors = useMemo(
    () =>
      allMonitors.filter((m) => {
        const key = (m.name || '').toLowerCase().replace(/[^a-z0-9]/g, '');
        return !Object.entries(features).some(
          ([service, enabled]) => !enabled && key.includes(service.replace('api-', ''))
        );
      }),
    [allMonitors, features]
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [countdown, setCountdown] = useState<number>(REFRESH_INTERVAL);
  const [heartbeatPoints, setHeartbeatPoints] = useState<number>(50);

  useEffect(() => {
    const computePoints = () => {
      const width = window.innerWidth;
      if (width < 640) return 22;
      if (width < 768) return 28;
      if (width < 1024) return 36;
      return 50;
    };

    const apply = () => setHeartbeatPoints(computePoints());
    apply();
    window.addEventListener('resize', apply);
    return () => window.removeEventListener('resize', apply);
  }, []);

  const fetchMonitors = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true);
      else setRefreshing(true);
      setError(null);
      try {
        const res = await fetch(`${API_URL}/status/monitors`, {
          headers: { Authorization: `Bearer ${keycloak?.token}` },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        setAllMonitors(Array.isArray(data.monitors) ? data.monitors : []);
        setLastUpdated(new Date());
        setCountdown(REFRESH_INTERVAL);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [keycloak?.token]
  );

  useEffect(() => {
    fetchMonitors();
    const id = setInterval(() => fetchMonitors(true), REFRESH_INTERVAL * 1000);
    return () => clearInterval(id);
  }, [fetchMonitors]);

  useEffect(() => {
    const id = setInterval(() => {
      setCountdown((prev) => (prev <= 1 ? REFRESH_INTERVAL : prev - 1));
    }, 1000);
    return () => clearInterval(id);
  }, []);

  const overall = overallStatus(monitors);

  const banner =
    overall === 'all_up'
      ? {
          icon: <CheckCircleIcon className="h-6 w-6 shrink-0" />,
          text: 'All systems operational',
          classes: 'border-success/30 bg-success/10 text-success',
        }
      : overall === 'some_down'
      ? {
          icon: <ExclamationCircleIcon className="h-6 w-6 shrink-0" />,
          text: 'Some systems are experiencing issues',
          classes: 'border-destructive/30 bg-destructive/10 text-destructive',
        }
      : {
          icon: <ClockIcon className="h-6 w-6 shrink-0" />,
          text: 'Checking system status…',
          classes: 'border-border bg-muted/40 text-muted-foreground',
        };

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<SignalIcon />}
        title="Status"
        description={
          lastUpdated
            ? `Last updated at ${formatTime(lastUpdated)}`
            : 'Real-time availability of the platform services.'
        }
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchMonitors(true)}
            disabled={refreshing}
          >
            <ArrowPathIcon className={cn(refreshing && 'animate-spin')} />
            Refresh
          </Button>
        }
      />

      {error && <ErrorAlert message={`Failed to load monitor data: ${error}`} />}

      {!error && !loading && monitors.length > 0 && (
        <div className={cn('flex items-center gap-3 rounded-lg border px-5 py-4 font-medium', banner.classes)}>
          {banner.icon}
          <span>{banner.text}</span>
        </div>
      )}

      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-24 w-full rounded-lg" />
          ))}
        </div>
      ) : monitors.length === 0 && !error ? (
        <EmptyState
          icon={<SignalIcon />}
          title="No monitors configured"
          description="There are currently no monitors set up for your account."
        />
      ) : (
        <>
          <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
            Services
          </h2>
          <div className="space-y-3">
            {monitors.map((m) => (
              <MonitorRow key={m.id} monitor={m} heartbeatPoints={heartbeatPoints} />
            ))}
          </div>
          <p className="text-xs text-muted-foreground text-center">Refresh in {countdown}s</p>
        </>
      )}
    </div>
  );
}
